import { Checkbox, InputCheckbox } from '@creditornot/cb-checkbox';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Input, InputAlert, Textarea } from '@creditornot/cb-input';
import { Radio, RadioCard } from '@creditornot/cb-radio';
import { MultiSelect, Select, SelectAsync } from '@creditornot/cb-select';
import { InputSwitch } from '@creditornot/cb-switch';
import R from 'ramda';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { PickingArea } from 'apis/picker-api/types';
import { ProductBrand } from 'apis/product-information-catalog/types';
import {
  Category,
  DietaryPreference,
  MandatoryWarning,
  MenuConfigs,
  ProductInformationSettings,
  UserRequirement,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { VatCategory } from 'apis/vat-categories/types';
import { RFFFieldErrorWrapper } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import { WeeklyScheduleInput } from 'common/components/weekly-schedules';
import { useDeposits, useLocaleUtils, usePluralization } from 'common/hooks';
import { localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import MultiImageUploadInput from 'modules/menu/components/ImageUploadInput/MultiImageUploadInput/MultiImageUploadInput';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';
import { VatCategories, VatFieldValue } from 'modules/menu/components/VatCategories';
import {
  PRODUCT_INFO_FIELD_MAX_LENGTH,
  PRODUCT_INFO_EXTENDED_FIELD_MAX_LENGTH,
  MORE_INFORMATION_FIELD_MAX_LENGTH,
  WOLT_SPECIALS_CATEGORY_ID,
  INGREDIENTS_FIELD_MAX_LENGTH,
  ITEM_MAX_IMAGE_COUNT,
} from 'modules/menu/constants';
import { useIsMinQuantityFieldEnabled } from 'modules/menu/hooks/useIsMinQuantityFieldEnabled';
import { mandatoryWarningsForCountry, mandatoryWarningTranslationMap } from 'modules/menu/mandatoryWarnings';

import AvailabilityDateTimePicker from './AvailabilityDateTimePicker';
import { CaffeineContentFields } from './CaffeineContentFields';
import DiscountFields from './DiscountFields';
import FeedField from './FeedField';
import FeedInputContainer from './FeedInputContainer';
import InputMonetary from './InputMonetary';
import NutritionFactsFields from './NutritionFactsFields';
import RestrictedItemFields from './RestrictedItemFields';
import UnitDetailsFormSection from './UnitDetailsFormSection';
import {
  FormValues,
  BasicDetails,
  AdvancedDetails,
  AvailabilityDetails,
  FeedFormValues,
  ItemConfigFormValues,
  PickingRouteFormValues,
} from './types';
import { MenuLocaleContext } from '../../../MenuLocaleContext';
import { MultiLevelSelect } from '../../../components/MultiLevelSelect';
import { UNASSIGNED_AREA_ID } from '../../constants';
import { ItemQuantity } from '../../types';
import BundleConfirmModal from '../BundleConfirmModal';
import InventoryMngmtFormSection from '../Item/InventoryMngmtFormSection';
import CommissionsFormSection from '../Item/ItemCommissions/ItemCommissionsFormSection';
import OptionBindingsFormSection from '../Item/OptionBindingsFormSection';

type Props = {
  allowCategoryChange: boolean;
  allowUserRequirements: boolean;
  enableWoltPlusToggle: boolean;
  categories: Category[];
  vatCategories: VatCategory[] | undefined;
  className?: string;
  currency: Currency;
  syncedFeedFields: string[];
  itemQuantity?: ItemQuantity;
  mode: 'create' | 'edit';
  nutritionFactsFormat: 'text' | 'table';
  productInfoSettings: ProductInformationSettings | undefined;
  pickingAreas: PickingArea[] | undefined;
  returnPolicies: MenuConfigs['item_return_policies'];
  brand: ProductBrand | null;
  loadBrandOptions: (search: string) => Promise<{ label: string; value: string }[]>;
  onChangeNutritionFactsFormat: (nutritionFactsFormat: 'text' | 'table') => void;
};

const NAME_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 5000;
const MAX_TEXTAREA_ROWS = 10;

const Section = styled.div`
  & + & {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const SectionTitle = styled.div`
  ${typographyCss.Title1()};
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledInputContainer = styled(InputContainer)`
  max-width: 392px;
`;

const StyledFeedInputContainer = styled(FeedInputContainer)`
  max-width: 392px;
`;

const AdvancedDetailsTextarea = styled(Textarea).attrs({
  autoresizeMaxRows: MAX_TEXTAREA_ROWS,
})``;

const StyledMultiImageUploadInput = styled(MultiImageUploadInput)`
  max-width: 392px;
`;

const FeedImage = styled.img`
  display: block;
  width: 90px;
  border-radius: 8px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  overflow: hidden;
  margin-block-end: 12px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled(Checkbox)`
  & + & {
    margin-block-start: 12px;
  }
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WeeklyScheduleWrapper = styled.div`
  max-width: 392px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding-block-start: 24px;
  margin-block-start: 24px;
`;

const StyledRadio = styled(Radio)`
  & + & {
    margin-block-start: 12px;
  }
`;

const StorageLocationSection = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StorageLocationHeader = styled.div`
  ${typographyCss.Title1()};
  margin-block-start: 16px;
  padding-block-end: 16px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StorageLocationInfo = styled.div`
  margin-block-start: 8px;
  ${typographyCss.Body3()};
  font-weight: normal;
  white-space: normal;
`;

const UnitDetailsSelectionWrapper = styled.div`
  display: inline-flex;
  flex-direction: row;
  width: 100%;
  margin-block-end: 8px;
`;

const StyledRadioCard = styled(RadioCard)`
  & + & {
    margin-inline-start: 8px;
  }
  width: 100%;
  padding: 8px 12px;
`;

const ColumnContainerWithGap = styled(ColumnContainer)`
  gap: 24px;
`;

const PickingFormRow = styled.div`
  display: flex;
`;

const PickingAreaSelect = styled(Select)`
  flex: 1;
`;

const PickingSequenceInput = styled(InputNumber)`
  width: 150px;
  margin-inline-start: 8px;
`;

const FormRowWrapper = styled.div`
  display: flex;
`;

const PurchaseQuantityWrapper = styled.label`
  width: 50%;
  & + & {
    margin-inline-start: 8px;
  }
`;

const ProductFamilyFormRow = styled(FormRow)`
  div {
    /* Ellipsis overflow text */
    min-width: 0;
  }
`;

const PurchaseQuantityLabel = styled.div`
  ${typographyCss.Body3()};
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 8px;
`;

const createCategoryOption = (category: Category, parentId: null | string, primaryLanguage: string) => ({
  value: category.id,
  label: localize(category.name, primaryLanguage),
  parentId,
  childrenIds: category.child_categories.map(subcategory => subcategory.id),
});

const ItemDetailsFormFields = ({
  allowCategoryChange,
  allowUserRequirements,
  enableWoltPlusToggle,
  categories,
  vatCategories,
  className,
  currency,
  syncedFeedFields,
  itemQuantity,
  mode,
  nutritionFactsFormat,
  productInfoSettings,
  pickingAreas,
  returnPolicies,
  brand,
  loadBrandOptions,
  onChangeNutritionFactsFormat,
}: Props) => {
  const intl = useIntl();
  const { getCurrencySymbol, getLocalizedUnit } = useLocaleUtils();
  const form = useForm<FormValues>();
  const { values, submitting } = useFormState<FormValues>();
  const { formatPluralMessage } = usePluralization();
  const { alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();
  const isMinQuantityFieldEnabled = useIsMinQuantityFieldEnabled();

  const { menuId } = useContext(MenuMetaContext);
  const { primaryLanguage, country } = useContext(MenuLocaleContext);
  const menuFeatures = useContext(MenuFeaturesContext);
  const menuPermissions = useContext(MenuPermissionsContext);

  const [bundleModalOpen, setBundleModalOpen] = useState(false);

  const { data: depositOptions } = useDeposits(country, menuFeatures.bottleDeposits);

  const hasStorageLocationEnabled = menuFeatures.hasPickerEnabled && menuFeatures.isRetail;

  const returnPoliciesOptions = useMemo(() => {
    if (!returnPolicies?.length || !menuFeatures.hasReturnPoliciesEnabled) {
      return [];
    }

    return returnPolicies.map(({ type, description, days }) => ({
      value: type,
      label: `${description} - ${formatPluralMessage({ id: 'menu-item.return-policy-days', count: days })}`,
    }));
  }, [formatPluralMessage, menuFeatures.hasReturnPoliciesEnabled, returnPolicies]);

  const categoryOptions = useMemo(
    () =>
      categories
        .filter(category => category.id !== WOLT_SPECIALS_CATEGORY_ID)
        .flatMap(category => [
          createCategoryOption(category, null, primaryLanguage),
          ...category.child_categories.map(subcategory =>
            createCategoryOption(subcategory, category.id, primaryLanguage),
          ),
        ]),
    [categories, primaryLanguage],
  );
  const productHierarchyOptions = useMemo(
    () =>
      productInfoSettings?.product_hierarchy_levels
        .map((level, _, array) => ({
          value: level.id,
          label: localize(level.name, intl.locale),
          secondaryContent: level.human_readable_id,
          parentId: level.parent_category_id,
          childrenIds: array.filter(item => item.parent_category_id === level.id).map(item => item.id),
          tags: level.tags,
        }))
        .sort((a, b) => Number(a.secondaryContent) - Number(b.secondaryContent)) || [],
    [intl.locale, productInfoSettings?.product_hierarchy_levels],
  );

  const pickingAreaOptions = useMemo(
    () => pickingAreas?.map(area => ({ value: area.id, label: area.name })),
    [pickingAreas],
  );

  const isOverTheCounterProductHierarchyLevel = useCallback(
    (value: string): boolean =>
      !!productHierarchyOptions.find(option => option.value === value)?.tags.includes('over_the_counter'),
    [productHierarchyOptions],
  );

  const isProductHierarchyLevelRestricted = useCallback(
    (value: string): boolean =>
      !!productHierarchyOptions.find(option => option.value === value)?.tags.includes('tobacco'),
    [productHierarchyOptions],
  );

  const userRequirementOptions: { value: UserRequirement; label: string }[] = useMemo(() => {
    return [
      { value: 'no_wolt_purchases', label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-wolt' }) },
      {
        value: 'no_franchise_purchases',
        label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-franchise' }),
      },
      {
        value: 'no_venue_purchases',
        label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-venue' }),
      },
    ];
  }, [intl]);

  const canEditVat = menuPermissions.itemVatChange && menuPermissions.contentEdit;

  const dietaryPreferenceOptions: { value: DietaryPreference; label: string }[] = useMemo(() => {
    return [
      {
        value: 'gluten-free',
        label: intl.formatMessage({ id: 'menu.dietaryPreferences.glutenFree' }),
      },
      {
        value: 'lactose-free',
        label: intl.formatMessage({ id: 'menu.dietaryPreferences.lactoseFree' }),
      },
      {
        value: 'halal',
        label: intl.formatMessage({ id: 'menu.dietaryPreferences.halal' }),
      },
      {
        value: 'kosher',
        label: intl.formatMessage({ id: 'menu.dietaryPreferences.kosher' }),
      },
      {
        value: 'vegan',
        label: intl.formatMessage({ id: 'menu.dietaryPreferences.vegan' }),
      },
      {
        value: 'vegetarian',
        label: intl.formatMessage({ id: 'menu.dietaryPreferences.vegetarian' }),
      },
    ];
  }, [intl]);

  const mandatoryWarningOptions: { value: MandatoryWarning; label: string }[] = useMemo(() => {
    return (
      mandatoryWarningsForCountry[country]?.map((warningForCountry: MandatoryWarning) => ({
        value: warningForCountry,
        label: intl.formatMessage({ id: mandatoryWarningTranslationMap[warningForCountry] }),
      })) || []
    );
  }, [country, intl]);

  const usageInstructionsMaxLength = menuFeatures.extendedUsageInstructions
    ? PRODUCT_INFO_EXTENDED_FIELD_MAX_LENGTH
    : PRODUCT_INFO_FIELD_MAX_LENGTH;

  const isFieldSynced = useCallback(
    (fieldName: keyof FeedFormValues) => syncedFeedFields.includes(fieldName),
    [syncedFeedFields],
  );

  const handleRestrictedPropertyChange = useCallback(
    ({
      productHierarchyLevelId,
      alcoholPercentage,
    }: {
      productHierarchyLevelId?: string | null;
      alcoholPercentage?: string | number;
    }) => {
      const isAlcoholPercentageExceedWarningLimit =
        typeof alcoholPercentage === 'number' && alcoholPercentage > alcoholWarningPermilleLimit;
      const isRestrictedProductFamily =
        productHierarchyLevelId && isProductHierarchyLevelRestricted(productHierarchyLevelId);

      if (!isAlcoholPercentageExceedWarningLimit && !isRestrictedProductFamily) {
        return;
      }

      form.batch(() => {
        form.change('isRestrictedItem', true);
        form.change('isAgeRestricted', true);
        form.change('ageLimit', undefined);
      });
    },
    [alcoholWarningPermilleLimit, form, isProductHierarchyLevelRestricted],
  );

  return (
    <div className={className}>
      <Section>
        <SectionTitle>
          <FormattedMessage id="menu-item.details-heading" />
        </SectionTitle>
        {menuFeatures.hasCatalogSource && (
          <>
            <FormRow label={intl.formatMessage({ id: 'menu.barcode-gtin' })}>
              <FeedField
                primaryLanguage={primaryLanguage}
                menuId={menuId}
                country={country}
                listedFieldNames={syncedFeedFields}
                nutritionSettings={productInfoSettings?.nutrition_values || []}
                onChangeNutritionFactsFormat={onChangeNutritionFactsFormat}
              />
            </FormRow>
            <FormRow label={intl.formatMessage({ id: 'menu.merchant-sku' })}>
              <Field<BasicDetails['merchantSku']> name="merchantSku">
                {field => (
                  <StyledInputContainer>
                    <Input
                      {...field.input}
                      disabled={!menuPermissions.contentEdit}
                      placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                    />
                  </StyledInputContainer>
                )}
              </Field>
            </FormRow>
          </>
        )}

        {allowCategoryChange && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.category' })}>
            <Field<BasicDetails['categoryId']> name="categoryId">
              {field => (
                <StyledInputContainer>
                  <MultiLevelSelect
                    {...field.input}
                    disabled={!menuPermissions.contentEdit}
                    options={categoryOptions}
                    onClearClick={() => field.input.onChange(null)}
                    placeholder={intl.formatMessage({ id: 'menu.selectOrSearch' })}
                  />
                </StyledInputContainer>
              )}
            </Field>
          </FormRow>
        )}
        <FormRow label={intl.formatMessage({ id: 'menu-item.name' })} labelHtmlFor="menu-item-name" required>
          <Field<BasicDetails['name']> name="name">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <StyledFeedInputContainer
                    name="name"
                    isSynced={isFieldSynced('name')}
                    characterCount={{ value: input.value.length, max: NAME_MAX_LENGTH }}
                  >
                    {({ disabled }) => (
                      <Input
                        {...input}
                        autoFocus={mode === 'create'}
                        maxLength={NAME_MAX_LENGTH}
                        spellCheck
                        disabled={!menuPermissions.contentEdit || disabled}
                        id="menu-item-name"
                      />
                    )}
                  </StyledFeedInputContainer>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>
        <ProductFamilyFormRow label={intl.formatMessage({ id: 'menu-item.product-family' })}>
          <Field<BasicDetails['productHierarchyLevelId']> name="productHierarchyLevelId">
            {field => (
              <StyledFeedInputContainer
                name="productHierarchyLevelId"
                isSynced={isFieldSynced('productHierarchyLevelId')}
              >
                {({ disabled }) => (
                  <MultiLevelSelect
                    {...field.input}
                    value={field.input.value ?? null}
                    onChange={value => {
                      field.input.onChange(value);

                      handleRestrictedPropertyChange({ productHierarchyLevelId: value });

                      if (
                        (!value || !isOverTheCounterProductHierarchyLevel(value)) &&
                        form.getFieldState('isRestrictedItem')?.value &&
                        form.getFieldState('isOTCRestricted')?.value === true
                      ) {
                        form.change('isRestrictedItem', false);
                        form.change('isOTCRestricted', false);
                      }
                    }}
                    onClearClick={() => field.input.onChange(undefined)}
                    options={productHierarchyOptions}
                    disabled={!menuPermissions.contentEdit || disabled}
                    placeholder={intl.formatMessage({ id: 'menu.selectOrSearch' })}
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </ProductFamilyFormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.brand' })}>
          <Field<BasicDetails['brandId']> name="brandId">
            {field => (
              <StyledFeedInputContainer name="brandId" isSynced={isFieldSynced('brandId')}>
                {({ disabled }) => (
                  <SelectAsync
                    {...field.input}
                    initialOption={
                      brand
                        ? { label: localize(brand.name.translations, brand.name.primaryLanguage), value: brand.id }
                        : undefined
                    }
                    clearable
                    loadOptions={loadBrandOptions}
                    disabled={!menuPermissions.contentEdit || disabled}
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.description' })}>
          <Field<BasicDetails['description']> name="description">
            {field => (
              <StyledFeedInputContainer
                name="description"
                isSynced={isFieldSynced('description')}
                characterCount={{ value: field.input.value.length, max: DESCRIPTION_MAX_LENGTH }}
              >
                {({ disabled }) => (
                  <Textarea
                    disabled={!menuPermissions.contentEdit || disabled}
                    {...field.input}
                    autoresizeMaxRows={MAX_TEXTAREA_ROWS}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                    rows={2} // Show a minimum of 2 rows to visually distinguish description from other inputs when it's empty
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        {menuFeatures.supportWeightedItems && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.item-type' })} required>
            <Field<BasicDetails['weightType']> name="weightType">
              {({ input }) => (
                <UnitDetailsSelectionWrapper>
                  {[
                    {
                      value: 'pre-packaged' as const,
                      disabled: !menuPermissions.contentEdit,
                      label: intl.formatMessage({ id: 'menu-item.unit-details-view-selection.pre-packaged.label' }),
                      subContent: intl.formatMessage({
                        id: 'menu-item.unit-details-view-selection.pre-packaged.sub-content',
                      }),
                    },
                    {
                      value: 'weighted-items' as const,
                      disabled: !menuPermissions.contentEdit,
                      label: intl.formatMessage({ id: 'menu-item.unit-details-view-selection.weighted-items.label' }),
                      subContent: intl.formatMessage({
                        id: 'menu-item.unit-details-view-selection.weighted-items.sub-content',
                      }),
                    },
                  ].map(({ value, label, subContent }) => (
                    <StyledRadioCard
                      key={value}
                      onChange={() => input.onChange(value)}
                      checked={input.value === value}
                      label={label}
                      subContent={subContent}
                    />
                  ))}
                </UnitDetailsSelectionWrapper>
              )}
            </Field>
            {values.optionBindings.length > 0 && values.weightType === 'weighted-items' && (
              <InputAlert variant="info">
                <FormattedMessage id="menu-item.unit-details-view-selection.options-warning" />
              </InputAlert>
            )}
          </FormRow>
        )}

        <FormRow label={intl.formatMessage({ id: 'menu-item.price' })} required>
          <Field<BasicDetails['price']> name="price">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <StyledInputContainer>
                    {values.weightType === 'pre-packaged' && (
                      <InputMonetary disabled={!menuPermissions.contentEdit} {...input} currency={currency} />
                    )}
                    {values.weightType === 'weighted-items' && (
                      <InputMonetary
                        {...input}
                        disabled={!menuPermissions.contentEdit}
                        suffixText={`${getCurrencySymbol(currency)}/${getLocalizedUnit('kilogram')}`}
                        currency={currency}
                      />
                    )}
                  </StyledInputContainer>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu-item.discount' })}>
          <StyledInputContainer>
            <DiscountFields
              disabled={!menuPermissions.contentEdit}
              currency={currency}
              isWeightedItem={values.weightType === 'weighted-items'}
            />
          </StyledInputContainer>
        </FormRow>

        <UnitDetailsFormSection isFieldSynced={isFieldSynced} currency={currency} weightType={values.weightType} />

        <FormRow label={intl.formatMessage({ id: 'menu-item.vat-label-edit' })} required>
          <Field<VatFieldValue> name="vat">
            {field => (
              <RFFFieldErrorWrapper field={field} showErrorImmediately>
                {input => (
                  <VatCategories {...input} vatCategories={vatCategories} disabled={!canEditVat} lang={intl.locale} />
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.alcoholPercentage' })} required>
          <Field<BasicDetails['alcoholPercentage']> name="alcoholPercentage">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <StyledFeedInputContainer name="alcoholPercentage" isSynced={isFieldSynced('alcoholPercentage')}>
                    {({ disabled }) => (
                      <InputNumber
                        {...input}
                        onChange={value => {
                          handleRestrictedPropertyChange({
                            alcoholPercentage: value,
                          });
                          input.onChange(value);
                        }}
                        disabled={!menuPermissions.contentEdit || disabled}
                        suffixText="%"
                        decimals={1}
                      />
                    )}
                  </StyledFeedInputContainer>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.caffeine' })}>
          <StyledInputContainer>
            <CaffeineContentFields />
          </StyledInputContainer>
        </FormRow>
        {menuPermissions.posIdField && (
          <FormRow label={intl.formatMessage({ id: 'menu.pos-id' })}>
            <Field<BasicDetails['posId']> name="posId">
              {field => (
                <StyledInputContainer>
                  <Input
                    {...field.input}
                    disabled={!menuPermissions.contentEdit}
                    placeholder={intl.formatMessage({ id: 'menu.pos-id-placeholder' })}
                  />
                </StyledInputContainer>
              )}
            </Field>
          </FormRow>
        )}
        {/* Don't show barcode/GTIN field here if feed integration is enabled, because the field is rendered at the top in that case. */}
        {menuFeatures.isRetail && !menuFeatures.hasCatalogSource && (
          <FormRow label={intl.formatMessage({ id: 'menu.barcode-gtin' })}>
            <Field<BasicDetails['barcodeGtin']> name="barcodeGtin">
              {field => (
                <RFFFieldErrorWrapper field={field}>
                  {input => (
                    <StyledInputContainer>
                      <Input
                        {...input}
                        disabled={!menuPermissions.contentEdit}
                        placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                      />
                    </StyledInputContainer>
                  )}
                </RFFFieldErrorWrapper>
              )}
            </Field>
          </FormRow>
        )}
        {/* Don't show merchant SKU field here if feed integration is enabled, because the field is rendered at the top in that case. */}
        {menuFeatures.isRetail && !menuFeatures.hasCatalogSource && (
          <FormRow label={intl.formatMessage({ id: 'menu.merchant-sku' })}>
            <Field<BasicDetails['merchantSku']> name="merchantSku">
              {field => (
                <StyledInputContainer>
                  <Input
                    {...field.input}
                    disabled={!menuPermissions.contentEdit}
                    placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                  />
                </StyledInputContainer>
              )}
            </Field>
          </FormRow>
        )}
        <FormRow label={intl.formatMessage({ id: 'menu-item.image' })}>
          <Field<BasicDetails['image']> name="image">
            {({ input }) => {
              return (
                <StyledFeedInputContainer name="image" isSynced={isFieldSynced('name')}>
                  {({ disabled }) => {
                    if (!disabled && menuPermissions.contentEdit) {
                      return <StyledMultiImageUploadInput {...input} imageCountLimit={ITEM_MAX_IMAGE_COUNT} />;
                    }

                    if (input.value.length > 0 && menuPermissions.contentEdit) {
                      return input.value.map(imageField =>
                        imageField.type === 'existing-image' ? (
                          <FeedImage key={imageField.imageUrl} src={imageField.imageUrl} />
                        ) : null,
                      );
                    }

                    return '-';
                  }}
                </StyledFeedInputContainer>
              );
            }}
          </Field>
        </FormRow>
      </Section>

      <Section>
        <SectionTitle>
          <FormattedMessage id={'menu-item.advanced-details'} />
        </SectionTitle>

        <FormRow label={intl.formatMessage({ id: 'menu.dietaryPreferences.title' })}>
          <Field<AdvancedDetails['dietaryPreferences']> name="dietaryPreferences">
            {field => (
              <StyledFeedInputContainer name="dietaryPreferences" isSynced={isFieldSynced('dietaryPreferences')}>
                {({ disabled }) => (
                  <MultiSelect
                    {...field.input}
                    options={dietaryPreferenceOptions}
                    disabled={!menuPermissions.contentEdit || disabled}
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.country-of-origin' })}>
          <Field<AdvancedDetails['countryOfOrigin']> name="countryOfOrigin">
            {field => (
              <StyledFeedInputContainer name="countryOfOrigin" isSynced={isFieldSynced('countryOfOrigin')}>
                {({ disabled }) => (
                  <Input
                    {...field.input}
                    placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                    disabled={!menuPermissions.contentEdit || disabled}
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.ingredients' })}>
          <Field<AdvancedDetails['ingredients']> name="ingredients">
            {field => (
              <StyledFeedInputContainer
                name="ingredients"
                isSynced={isFieldSynced('ingredients')}
                characterCount={{ value: field.input.value.length, max: INGREDIENTS_FIELD_MAX_LENGTH }}
              >
                {({ disabled }) => (
                  <AdvancedDetailsTextarea
                    {...field.input}
                    maxLength={INGREDIENTS_FIELD_MAX_LENGTH}
                    disabled={!menuPermissions.contentEdit || disabled}
                    placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.additives' })}>
          <Field<AdvancedDetails['additives']> name="additives">
            {field => (
              <StyledFeedInputContainer
                name="additives"
                isSynced={isFieldSynced('additives')}
                characterCount={{ value: field.input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
              >
                {({ disabled }) => (
                  <AdvancedDetailsTextarea
                    {...field.input}
                    maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                    placeholder={intl.formatMessage({ id: 'menu.additives-placeholder' })}
                    disabled={!menuPermissions.contentEdit || disabled}
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.allergens' })}>
          <Field<AdvancedDetails['allergens']> name="allergens">
            {field => (
              <StyledFeedInputContainer
                name="allergens"
                isSynced={isFieldSynced('allergens')}
                characterCount={{ value: field.input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
              >
                {({ disabled }) => (
                  <AdvancedDetailsTextarea
                    {...field.input}
                    maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                    disabled={!menuPermissions.contentEdit || disabled}
                    placeholder={intl.formatMessage({ id: 'menu.allergens-placeholder' })}
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        <NutritionFactsFields
          nutritionSettings={productInfoSettings?.nutrition_values || []}
          nutritionValues={values.nutritionValues}
          activeFieldType={nutritionFactsFormat}
          isSynced={
            isFieldSynced('nutritionValues') ||
            isFieldSynced('nutritionFacts') ||
            isFieldSynced('nutritionValuesBaseline')
          }
          onChangeFieldType={onChangeNutritionFactsFormat}
        />

        <FormRow label={intl.formatMessage({ id: 'menu.usage-instructions' })}>
          <Field<AdvancedDetails['usageInstructions']> name="usageInstructions">
            {field => (
              <StyledFeedInputContainer
                name="usageInstructions"
                isSynced={isFieldSynced('usageInstructions')}
                characterCount={{ value: field.input.value.length, max: usageInstructionsMaxLength }}
              >
                {({ disabled }) => (
                  <Textarea
                    {...field.input}
                    autoresizeMaxRows={MAX_TEXTAREA_ROWS}
                    disabled={!menuPermissions.contentEdit || disabled}
                    maxLength={usageInstructionsMaxLength}
                    placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.more-information' })}>
          <Field<AdvancedDetails['moreInformation']> name="moreInformation">
            {field => (
              <StyledFeedInputContainer
                name="moreInformation"
                isSynced={isFieldSynced('moreInformation')}
                characterCount={{ value: field.input.value.length, max: MORE_INFORMATION_FIELD_MAX_LENGTH }}
              >
                {({ disabled }) => (
                  <Textarea
                    {...field.input}
                    autoresizeMaxRows={MAX_TEXTAREA_ROWS}
                    disabled={!menuPermissions.contentEdit || disabled}
                    maxLength={MORE_INFORMATION_FIELD_MAX_LENGTH}
                    placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.producer-information' })}>
          <Field<AdvancedDetails['producerInfo']> name="producerInfo">
            {field => (
              <StyledFeedInputContainer
                name="producerInfo"
                isSynced={isFieldSynced('producerInfo')}
                characterCount={{ value: field.input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
              >
                {({ disabled }) => (
                  <AdvancedDetailsTextarea
                    {...field.input}
                    maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                    disabled={!menuPermissions.contentEdit || disabled}
                    placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.distributor-information' })}>
          <Field<AdvancedDetails['distributorInfo']> name="distributorInfo">
            {field => (
              <StyledFeedInputContainer
                name="distributorInfo"
                isSynced={isFieldSynced('distributorInfo')}
                characterCount={{ value: field.input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
              >
                {({ disabled }) => (
                  <AdvancedDetailsTextarea
                    {...field.input}
                    maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                    disabled={!menuPermissions.contentEdit || disabled}
                    placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                    spellCheck
                  />
                )}
              </StyledFeedInputContainer>
            )}
          </Field>
        </FormRow>

        {/* Bundles are only enabled for Germany, but also show the field if it's true to allow turning it off */}
        {(menuFeatures.bundleItems || values.bundleOffer) && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.bundle-offer' })}>
            <Field name="bundleOffer" type="checkbox">
              {field => (
                <>
                  <InputCheckbox
                    {...field.input}
                    disabled={!menuPermissions.contentEdit}
                    onChange={event => {
                      if (event.currentTarget.checked) {
                        setBundleModalOpen(true);
                      } else {
                        field.input.onChange(false);
                      }
                    }}
                  />
                  <BundleConfirmModal
                    isOpen={bundleModalOpen}
                    onClose={() => setBundleModalOpen(false)}
                    onConfirm={() => {
                      field.input.onChange(true);
                      setBundleModalOpen(false);
                    }}
                  />
                </>
              )}
            </Field>
          </FormRow>
        )}

        {values.weightType !== 'weighted-items' && menuFeatures.bottleDeposits && (
          <FormRow
            label={intl.formatMessage({ id: 'menu-item.deposit' })}
            info={intl.formatMessage({ id: 'menu-item.deposit-tooltip' })}
          >
            <Field<AdvancedDetails['depositOption']> name="depositOption">
              {field => (
                <StyledInputContainer>
                  <Select
                    {...field.input}
                    disabled={!menuPermissions.contentEdit}
                    options={depositOptions}
                    clearable={false}
                  />
                </StyledInputContainer>
              )}
            </Field>
          </FormRow>
        )}

        {menuFeatures.hasReturnPoliciesEnabled && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.return-policy' })}>
            <StyledInputContainer>
              <Field<string> name="returnPolicy">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Select {...input} clearable options={returnPoliciesOptions} />}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </StyledInputContainer>
          </FormRow>
        )}
        {menuFeatures.mandatoryWarnings && (
          <FormRow label={intl.formatMessage({ id: 'menu.mandatoryWarnings.title' })}>
            <Field<AdvancedDetails['mandatoryWarnings']> name="mandatoryWarnings">
              {field => (
                <StyledFeedInputContainer name="mandatoryWarnings" isSynced={isFieldSynced('mandatoryWarnings')}>
                  {({ disabled }) => (
                    <MultiSelect
                      {...field.input}
                      options={mandatoryWarningOptions}
                      disabled={!menuPermissions.contentEdit || disabled}
                    />
                  )}
                </StyledFeedInputContainer>
              )}
            </Field>
          </FormRow>
        )}

        {((menuFeatures.overTheCounter && values.isOTCRestricted && menuFeatures.overTheCounterHyperlinkAllowed) ||
          menuFeatures.externalInfoHyperlinkAllowed) && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.external-item-link.form-row-label' })}>
            <ColumnContainerWithGap>
              <Field<string> name="externalInfoHyperlink.hyperlink">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => (
                      <StyledInputContainer
                        label={intl.formatMessage({ id: 'menu-item.external-item-link.hyperlink-label' })}
                      >
                        <Input
                          {...input}
                          placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                          disabled={!menuPermissions.contentEdit}
                        />
                      </StyledInputContainer>
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
              <Field<string> name="externalInfoHyperlink.text">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => (
                      <StyledInputContainer
                        characterCount={{ value: field.input.value.length, max: 80 }}
                        label={intl.formatMessage({ id: 'menu-item.external-item-link.text-label' })}
                      >
                        <Input
                          {...input}
                          maxLength={80}
                          placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                          disabled={!menuPermissions.contentEdit}
                        />
                      </StyledInputContainer>
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </ColumnContainerWithGap>
          </FormRow>
        )}
      </Section>

      <Section>
        <SectionTitle>
          <FormattedMessage id={'menu-item.availability-and-restrictions'} />
        </SectionTitle>

        <FormRow label={intl.formatMessage({ id: 'menu-item.enabled' })}>
          <Field<AvailabilityDetails['enabled']> name="enabled">
            {({ input }) => (
              <InputSwitch
                checked={input.value}
                disabled={!menuPermissions.contentEdit}
                onChange={() => {
                  input.onChange(!input.value);
                }}
              />
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'special.availability' })}>
          <Field<AvailabilityDetails['availabilityPeriod']> name="availabilityPeriod">
            {field => (
              <RFFFieldErrorWrapper field={field} showErrorImmediately>
                {input => (
                  <>
                    <StyledInputContainer
                      label={intl.formatMessage({ id: 'special.availability-picker-starting-label' })}
                      style={{ marginBlockEnd: '12px' }}
                    >
                      <AvailabilityDateTimePicker
                        disabled={!menuPermissions.contentEdit}
                        onChange={value => {
                          input.onChange({ ...input.value, start: value });
                        }}
                        value={input.value.start}
                      />
                    </StyledInputContainer>
                    <StyledInputContainer
                      label={intl.formatMessage({ id: 'special.availability-picker-ending-label' })}
                    >
                      <AvailabilityDateTimePicker
                        disabled={!menuPermissions.contentEdit}
                        onChange={value => {
                          input.onChange({ ...input.value, end: value });
                        }}
                        value={input.value.end}
                      />
                    </StyledInputContainer>
                  </>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu-item.time-available' })}>
          <ColumnContainer>
            <Field<AvailabilityDetails['useWeeklyAvailability']> name="useWeeklyAvailability">
              {({ input }) => (
                <RadioWrapper>
                  <StyledRadio
                    disabled={!menuPermissions.contentEdit}
                    checked={!input.value}
                    onChange={() => input.onChange(!input.value)}
                  >
                    <FormattedMessage id="menu-item.time-available-always" />
                  </StyledRadio>
                  <StyledRadio
                    disabled={!menuPermissions.contentEdit}
                    checked={input.value}
                    onChange={() => input.onChange(!input.value)}
                  >
                    <FormattedMessage id="menu-item.time-available-sometimes" />
                  </StyledRadio>
                </RadioWrapper>
              )}
            </Field>

            {values.useWeeklyAvailability && menuPermissions.contentEdit && (
              <Field<AvailabilityDetails['weeklyAvailability']> name="weeklyAvailability" isEqual={R.equals}>
                {({ input, meta }) => (
                  <WeeklyScheduleWrapper>
                    <WeeklyScheduleInput
                      onChange={input.onChange}
                      pristine={!!meta.pristine}
                      select24HoursLabel={intl.formatMessage({ id: 'weeklyScheduleInput.select24Hours' })}
                      value={input.value}
                    />
                  </WeeklyScheduleWrapper>
                )}
              </Field>
            )}
          </ColumnContainer>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu-item.time-visible' })}>
          <ColumnContainer>
            <Field<AvailabilityDetails['useWeeklyVisibility']> name="useWeeklyVisibility">
              {({ input }) => (
                <RadioWrapper>
                  <StyledRadio
                    disabled={!menuPermissions.contentEdit}
                    checked={!input.value}
                    onChange={() => input.onChange(!input.value)}
                  >
                    <FormattedMessage id="menu-item.time-visible-always" />
                  </StyledRadio>
                  <StyledRadio
                    disabled={!menuPermissions.contentEdit}
                    checked={input.value}
                    onChange={() => input.onChange(!input.value)}
                  >
                    <FormattedMessage id="menu-item.time-visible-sometimes" />
                  </StyledRadio>
                </RadioWrapper>
              )}
            </Field>

            {values.useWeeklyVisibility && menuPermissions.contentEdit && (
              <Field<AvailabilityDetails['weeklyVisibility']> name="weeklyVisibility" isEqual={R.equals}>
                {({ input, meta }) => (
                  <WeeklyScheduleWrapper>
                    <WeeklyScheduleInput
                      onChange={input.onChange}
                      pristine={!!meta.pristine}
                      select24HoursLabel={intl.formatMessage({ id: 'weeklyScheduleInput.select24Hours' })}
                      value={input.value}
                    />
                  </WeeklyScheduleWrapper>
                )}
              </Field>
            )}
          </ColumnContainer>
        </FormRow>

        {allowUserRequirements && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.ftu-restriction-label' })}>
            <Field<AvailabilityDetails['userRequirement']> name="userRequirement">
              {field => (
                <StyledInputContainer>
                  <Select
                    {...field.input}
                    disabled={!menuPermissions.contentEdit}
                    clearable
                    options={userRequirementOptions}
                  />
                </StyledInputContainer>
              )}
            </Field>
          </FormRow>
        )}

        {enableWoltPlusToggle && menuFeatures.hasWoltPlusSpecialsEnabled && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.wolt-plus-only.label' })}>
            <Field<AvailabilityDetails['isWoltPlusOnly']> name="isWoltPlusOnly">
              {field => (
                <RFFFieldErrorWrapper field={field}>
                  {input => (
                    <InputSwitch
                      checked={input.value}
                      disabled={!menuPermissions.contentEdit}
                      onChange={() => {
                        const newValue = !input.value;
                        input.onChange(newValue);
                      }}
                    />
                  )}
                </RFFFieldErrorWrapper>
              )}
            </Field>
          </FormRow>
        )}

        <FormRow label={intl.formatMessage({ id: 'menu-item.per-purchase' })}>
          <FormRowWrapper>
            {isMinQuantityFieldEnabled && (
              <PurchaseQuantityWrapper>
                <PurchaseQuantityLabel>
                  {intl.formatMessage({ id: 'menu-item.min-quantity-per-purchase' })}
                </PurchaseQuantityLabel>
                <Field<AvailabilityDetails['minQuantityPerPurchase']> name="minQuantityPerPurchase">
                  {field => (
                    <RFFFieldErrorWrapper field={field} showErrorImmediately>
                      {input => (
                        <StyledInputContainer>
                          <InputNumber
                            {...input}
                            disabled={!menuPermissions.contentEdit}
                            suffixText={
                              values.sellByWeightInputType === 'grams' && values.weightType === 'weighted-items'
                                ? getLocalizedUnit('gram')
                                : ''
                            }
                          />
                        </StyledInputContainer>
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </PurchaseQuantityWrapper>
            )}
            <PurchaseQuantityWrapper>
              <PurchaseQuantityLabel>
                {intl.formatMessage({ id: 'menu-item.max-quantity-per-purchase' })}
              </PurchaseQuantityLabel>
              <Field<AvailabilityDetails['maxQuantityPerPurchase']> name="maxQuantityPerPurchase">
                {field => (
                  <RFFFieldErrorWrapper field={field} showErrorImmediately>
                    {input => (
                      <StyledInputContainer>
                        <InputNumber
                          {...input}
                          disabled={!menuPermissions.contentEdit}
                          suffixText={
                            values.sellByWeightInputType === 'grams' && values.weightType === 'weighted-items'
                              ? getLocalizedUnit('gram')
                              : ''
                          }
                        />
                      </StyledInputContainer>
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </PurchaseQuantityWrapper>
          </FormRowWrapper>
        </FormRow>

        <RestrictedItemFields productHierarchyOptions={productHierarchyOptions} />

        <FormRow label={intl.formatMessage({ id: 'menu-item.deliveryMethods' })}>
          <Field<AvailabilityDetails['enableEatin']> name="enableEatin">
            {({ input }) => (
              <StyledCheckbox
                disabled={!menuPermissions.contentEdit}
                checked={input.value}
                onChange={() => input.onChange(!input.value)}
              >
                <FormattedMessage id="delivery-types.eatin" />
              </StyledCheckbox>
            )}
          </Field>

          <Field<AvailabilityDetails['enableTakeaway']> name="enableTakeaway">
            {({ input }) => (
              <StyledCheckbox
                disabled={!menuPermissions.contentEdit}
                checked={input.value}
                onChange={() => input.onChange(!input.value)}
              >
                <FormattedMessage id="delivery-types.takeaway" />
              </StyledCheckbox>
            )}
          </Field>

          <Field<AvailabilityDetails['enableDelivery']> name="enableDelivery">
            {({ input }) => (
              <StyledCheckbox
                disabled={!menuPermissions.contentEdit}
                checked={input.value}
                onChange={() => input.onChange(!input.value)}
              >
                <FormattedMessage id="delivery-types.homedelivery" />
              </StyledCheckbox>
            )}
          </Field>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu-item.courierRestrictions' })}>
          <Field<AvailabilityDetails['disableBikeDelivery']> name="disableBikeDelivery">
            {({ input }) => (
              <StyledCheckbox
                disabled={!menuPermissions.contentEdit}
                checked={input.value}
                onChange={() => input.onChange(!input.value)}
              >
                <FormattedMessage id="menu-item.disableBikes" />
              </StyledCheckbox>
            )}
          </Field>

          <Field<AvailabilityDetails['disableCarDelivery']> name="disableCarDelivery">
            {({ input }) => (
              <StyledCheckbox
                disabled={!menuPermissions.contentEdit}
                checked={input.value}
                onChange={() => input.onChange(!input.value)}
              >
                <FormattedMessage id="menu-item.disableCars" />
              </StyledCheckbox>
            )}
          </Field>

          <Field<AvailabilityDetails['disableMotorcycleDelivery']> name="disableMotorcycleDelivery">
            {({ input }) => (
              <StyledCheckbox
                disabled={!menuPermissions.contentEdit}
                checked={input.value}
                onChange={() => input.onChange(!input.value)}
              >
                <FormattedMessage id="menu-item.disableMotorcycles" />
              </StyledCheckbox>
            )}
          </Field>
        </FormRow>
      </Section>

      {hasStorageLocationEnabled && (
        <StorageLocationSection>
          <StorageLocationHeader>
            <FormattedMessage id="menu-item.picking-route.title" />
            <StorageLocationInfo>
              <FormattedMessage id={'menu-item.picking-route.subtitle'} />
            </StorageLocationInfo>
          </StorageLocationHeader>
          <FormRow label={intl.formatMessage({ id: 'menu-item.picking-route.picking-area' })}>
            <PickingFormRow>
              <Field<PickingRouteFormValues['pickingAreaId']> name="pickingAreaId">
                {field => (
                  <PickingAreaSelect
                    {...field.input}
                    options={pickingAreaOptions}
                    disabled={!menuPermissions.contentEdit}
                    placeholder={intl.formatMessage({ id: 'menu-item.picking-route.picking-placeholder' })}
                  />
                )}
              </Field>
              <Field<PickingRouteFormValues['pickingItemSequence']> name="pickingItemSequence">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => (
                      <PickingSequenceInput
                        {...input}
                        disabled={!menuPermissions.contentEdit || values.pickingAreaId === UNASSIGNED_AREA_ID}
                        placeholder={intl.formatMessage({ id: 'menu-item.picking-route.sequence-placeholder' })}
                      />
                    )}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </PickingFormRow>
          </FormRow>
          <FormRow label={intl.formatMessage({ id: 'menu-item.picking-route.location' })}>
            <Field<ItemConfigFormValues['location']> name="location">
              {field => (
                <Input
                  {...field.input}
                  disabled={!menuPermissions.contentEdit}
                  placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                />
              )}
            </Field>
          </FormRow>
        </StorageLocationSection>
      )}

      <InventoryMngmtFormSection
        inventoryMode={values.inventoryMode}
        disabled={submitting}
        reservedItems={itemQuantity?.reserved}
        availableItems={itemQuantity?.available}
        sellByWeightInputType={values.sellByWeightInputType}
        isWeightBasedItem={values.weightType === 'weighted-items'}
      />

      {mode === 'edit' && (
        <Section>
          <CommissionsFormSection disabled={!menuPermissions.customCommissionEdit} />
        </Section>
      )}

      <OptionBindingsFormSection isWeightedItem={values.weightType === 'weighted-items'} />
    </div>
  );
};

export default ItemDetailsFormFields;
